'use client';

import React, { useEffect, useRef } from 'react';
import chatBg from '../assets/chatting-bg.png';

export default function ChattingSection(): JSX.Element {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            stepsRef.current.forEach((step, i) => {
              setTimeout(() => {
                step?.classList.add('step-visible');
                if (i === 1) step?.classList.add('step-raised');
              }, i * 800);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.6 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .step-card {
          opacity: 0;
          transform: scale(0.5) translateY(40px);
          transition: opacity 0.75s cubic-bezier(0.34, 1.56, 0.64, 1),
                      transform 0.75s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .step-visible {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
        .step-visible.step-raised {
          transform: scale(1) translateY(-64px);
        }
        @media (max-width: 1023px) {
          .step-visible.step-raised {
            transform: scale(1) translateY(0);
          }
        }
      `}</style>

      <section
        ref={sectionRef}
        className="min-h-screen bg-transparent flex items-center justify-center px-8 py-20 relative" id="Chat"
      >
        <div className='absolute left-20 top-24 h-10 w-10 p-2 bg-[#6A35E0] shadow-[4px_4px_0_0_#000] flex justify-center items-center z-10'>
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.8281 2H7.82807M18.8281 2V12.5M18.8281 2L2.82807 17.5" stroke="#F9FBFD" strokeWidth="4" strokeLinecap="square"/>
          </svg>
        </div>

        <svg width="137" height="178" viewBox="0 0 137 178" fill="none" xmlns="http://www.w3.org/2000/svg" className='absolute top-0 left-0'>
          <path d="M1 175.537C1 175.537 88.5 184.557 96 135.398C103.5 86.2389 73 102.024 73 59.179C73 16.3342 135.5 1.00024 135.5 1.00024" stroke="#FF803E" strokeWidth="2" strokeLinecap="round" strokeDasharray="7 7"/>
        </svg>

        <div className="w-full max-w-6xl relative">

          {/* Header */}
          <div className="text-center lg:mb-48 sm:mb-24 mb-16 lg:mt-0 mt-28">
            <h2 className="md:text-5xl sm:text-4xl text-3xl lg:text-6xl font-bold text-[#0F172A] font-primary">
              Start chatting in 
              <span className="text-[#059669]"> 10 seconds</span>.
            </h2>
          </div>

          {/* Steps Wrapper */}
          <div className="relative flex items-center justify-center">

            {/* Curved Dotted Lines */}
            <svg width="195" height="62" viewBox="0 0 195 62" fill="none" xmlns="http://www.w3.org/2000/svg" className='absolute xl:left-60 left-60  -top-8 lg:flex hidden'>
              <path d="M0.316132 52.3523C0.316132 52.3523 76.8161 77.8523 115.316 35.8523C152.995 -5.25188 194.316 1.35253 194.316 1.35253" stroke="#E1B840" strokeWidth="2" strokeDasharray="9 9" />
            </svg>

            <svg width="166" height="62" viewBox="0 0 166 62" fill="none" xmlns="http://www.w3.org/2000/svg" className='absolute xl:right-72 right-72 -top-8 lg:flex hidden'>
              <path d="M165.249 52.8566C165.249 52.8566 91.7487 78.0175 67.4394 36.3731C41.1803 -8.61157 0.248657 1.90774 0.248657 1.90774" stroke="#E1B840" strokeWidth="2" strokeDasharray="9 9" />
            </svg>

            {/* Cards */}
            <div className="relative z-10 grid lg:grid-cols-3 xl:gap-10 lg:gap-2 sm:gap-6 gap-10 w-full xl:max-w-5xl lg:max-w-4xl max-w-5xl">

              {/* Step 1 */}
              <div
                ref={(el) => { stepsRef.current[0] = el; }}
                className="step-card flex flex-col sm:items-start items-center text-center xl:ml-0 sm:ml-6 ml-0"
              >
                <div className="flex items-center gap-0 mb-6 relative">
                  <div className="sm:w-14 sm:h-14 w-12 h-12 rounded-full font-primary text-[#f8fafc] bg-[#059669] flex items-center justify-center lg:text-xl sm:text-lg text-base font-bold shadow-[4px_4px_0px_0px_#02553B] absolute sm:-left-10 -left-8">
                    1
                  </div>
                  <div className="bg-[#059669] text-[#f8fafc] px-8 py-2 lg:text-xl sm:text-lg text-base font-semibold shadow-[4px_4px_0px_0px_#02553B] whitespace-nowrap font-primary">
                    Pick a name
                  </div>
                </div>
                <p className="text-sm text-[#64748B] max-w-[200px] font-primary sm:text-left text-center ml-0">
                  No email or password. Just choose a display name.
                </p>
              </div>

              {/* Step 2 — removed lg:translate-y-[-72px], now handled via JS + CSS */}
              <div
                ref={(el) => { stepsRef.current[1] = el; }}
                className="step-card flex flex-col items-center text-center"
              >
                <div className="flex items-center gap-0 mb-6 relative">
                  <div className="sm:w-14 sm:h-14 w-12 h-12 rounded-full font-primary text-[#f8fafc] bg-[#FF803E] flex items-center justify-center lg:text-xl sm:text-lg text-base font-bold shadow-[4px_4px_0px_0px_#B85B2B] absolute sm:-left-10 -left-8">
                    2
                  </div>
                  <div className="bg-[#FF803E] text-[#f8fafc] px-8 py-2 lg:text-xl sm:text-lg text-base font-semibold shadow-[4px_4px_0px_0px_#B85B2B] whitespace-nowrap font-primary">
                    Set your language
                  </div>
                </div>
                <p className="text-sm text-[#64748B] max-w-[200px] font-primary sm:text-left text-center ml-0">
                  Tell us what language you speak. That's it.
                </p>
              </div>

              {/* Step 3 */}
              <div
                ref={(el) => { stepsRef.current[2] = el; }}
                className="step-card flex flex-col sm:items-end items-center text-center"
              >
                <div className="flex items-center gap-0 mb-6 relative">
                  <div className="sm:w-14 sm:h-14 w-12 h-12 rounded-full font-primary text-[#f8fafc] bg-[#6A35E0] flex items-center justify-center lg:text-xl sm:text-lg text-base font-bold shadow-[4px_4px_0px_0px_#573D92] absolute sm:-left-10 -left-8">
                    3
                  </div>
                  <div className="bg-[#6A35E0] text-[#f8fafc] px-8 py-2 lg:text-xl sm:text-lg text-base font-semibold shadow-[4px_4px_0px_0px_#573D92] whitespace-nowrap font-primary">
                    Start chatting
                  </div>
                </div>
                <p className="text-sm text-[#64748B] max-w-[200px] font-primary sm:text-left text-center sm:ml-6 ml-0">
                  Join or create a room. Every message is translated live.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
}